import { useEffect, useState } from "react";
import { Users, Truck, Package, Wallet, AlertCircle, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { formatCHF, STATUS_LABEL, STATUS_TONE } from "@/lib/format";
import { toast } from "sonner";
import ConversionWidget from "@/components/admin/ConversionWidget";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(null);

  const loadStale = () => {
    api.get("/admin/orders/stale")
      .then(({ data }) => setStale(data))
      .catch(() => setStale(null));
  };

  useEffect(() => {
    api.get("/admin/stats")
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    loadStale();
    // Refresh stale ogni 30s
    const t = setInterval(loadStale, 30000);
    return () => clearInterval(t);
  }, []);

  const forceRelease = async (orderId) => {
    if (!window.confirm("Forzare il rilascio dell'ordine? Tornerà visibile ad altri driver.")) return;
    try {
      await api.post(`/admin/orders/${orderId}/force-release`, {});
      toast.success("Ordine rilasciato. Tornato in coda.");
      loadStale();
    } catch (e) {
      toast.error("Errore durante il rilascio: " + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading || !stats) return <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>;

  const cards = [
    { i: Users, l: "Utenti totali", v: stats.users_total },
    { i: Truck, l: "Trasportatori", v: stats.drivers_total },
    { i: Package, l: "Ordini totali", v: stats.total_orders },
    { i: Wallet, l: "Addebiti sulle consegne", v: formatCHF(stats.delivered_revenue) },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <p className="overline">Admin</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">Dashboard</h1>
      </div>

      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.l} className="card-soft p-5">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue"><c.i className="h-5 w-5" /></span>
            <p className="overline mt-4">{c.l}</p>
            <p className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">{c.v}</p>
          </div>
        ))}
      </section>

      {/* Phase 43.57 — Funnel conversion out-of-rides */}
      <ConversionWidget />

      <section className="grid lg:grid-cols-2 gap-5">
        <div className="card-soft p-6">
          <h2 className="font-display text-lg font-bold tracking-tight">Ordini per stato</h2>
          <div className="mt-4 space-y-3">
            {Object.entries(stats.by_status).length === 0 && (
              <p className="text-sm text-dapnow-muted">Nessun ordine ancora.</p>
            )}
            {Object.entries(stats.by_status).map(([s, c]) => {
              const tone = STATUS_TONE[s] || STATUS_TONE.pending;
              const pct = stats.total_orders ? Math.round((c / stats.total_orders) * 100) : 0;
              return (
                <div key={s}>
                  <div className="flex justify-between text-sm">
                    <span className={`pill ${tone.bg} ${tone.fg}`}><span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />{STATUS_LABEL[s] || s}</span>
                    <span className="font-display font-semibold">{c}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-dapnow-line overflow-hidden">
                    <div className={`h-full ${tone.dot}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card-soft p-6">
          <h2 className="font-display text-lg font-bold tracking-tight">Riepilogo finanziario</h2>
          <p className="mt-2 text-xs text-dapnow-muted">Importi finali delle consegne completate, al netto dei crediti. Acquisti di pacchetti e abbonamenti esclusi. La ripartizione storica va riconciliata con la remunerazione della flotta interna fuori da Stripe.</p>
          <div className="mt-4 space-y-3 text-sm">
            <Row k="Addebiti consegne completate" v={formatCHF(stats.delivered_revenue)} />
            <Row k="Commissione storica da riconciliare" v={formatCHF(stats.platform_commission)} highlight />
            <Row k="Clienti registrati" v={stats.customers_total} />
            <Row k="Trasportatori in attesa" v={stats.pending_drivers} alert={stats.pending_drivers > 0} />
          </div>
        </div>
      </section>

      {stats.pending_drivers > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3 text-sm text-amber-900">
          <AlertCircle className="h-5 w-5" />
          <p><strong>{stats.pending_drivers}</strong> trasportatori attendono la verifica documenti.</p>
        </div>
      )}

      {/* Phase 37 — Coda ordini in attesa di driver (status=pending, nessuno ancora accettato) */}
      {(stats.by_status?.pending || 0) > 0 && (
        <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4 flex items-start gap-3 text-sm text-orange-900" data-testid="admin-no-driver-queue-alert">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">
              <strong>{stats.by_status.pending}</strong> {stats.by_status.pending === 1 ? "ordine in attesa di driver" : "ordini in attesa di driver"}
            </p>
            <p className="text-xs mt-0.5">
              Nessun trasportatore ha ancora accettato. Verifica disponibilità o assegna manualmente dalla sezione Ordini.
            </p>
          </div>
          <a href="/admin/orders?status=pending" className="text-xs font-bold underline" data-testid="admin-no-driver-queue-link">
            Vedi ordini
          </a>
        </div>
      )}

      {/* Phase 39 — Driver inattivo (accepted da > threshold min) */}
      {stale && stale.count > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="admin-stale-orders-alert">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold">
                <strong>{stale.count}</strong> {stale.count === 1 ? "ordine bloccato dopo accettazione" : "ordini bloccati dopo accettazione"} (driver fermo da oltre {stale.threshold_minutes} min)
              </p>
              <p className="text-xs mt-0.5">
                Il driver ha accettato ma non sta procedendo verso il ritiro. Puoi rilasciare l'ordine per renderlo disponibile ad altri trasportatori.
              </p>
            </div>
          </div>
          <ul className="mt-3 space-y-2" data-testid="admin-stale-orders-list">
            {stale.items.slice(0, 5).map((o) => (
              <li key={o.id} className="rounded-xl bg-white/70 border border-amber-100 px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs">
                  <span className="font-mono font-semibold">#{o.id.slice(0,8).toUpperCase()}</span>
                  <span className="ml-2 text-amber-700">{o.driver_name || "—"}</span>
                  <span className="ml-2 text-amber-700">· da {new Date(o.accepted_at).toLocaleString("it-CH", { dateStyle: "short", timeStyle: "short" })}</span>
                </div>
                <button
                  onClick={() => forceRelease(o.id)}
                  data-testid={`admin-force-release-${o.id}`}
                  className="text-xs font-bold rounded-lg bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5"
                >
                  Rilascia ordine
                </button>
              </li>
            ))}
            {stale.count > 5 && (
              <li className="text-xs text-amber-700 px-3 italic">…e altri {stale.count - 5}.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, highlight, alert }) {
  return (
    <div className="flex justify-between items-baseline border-b border-dapnow-line pb-3 last:border-0 last:pb-0">
      <span className={`overline text-[10px] ${alert ? "text-amber-700" : ""}`}>{k}</span>
      <span className={`font-display font-semibold ${highlight ? "text-dapnow-blue" : ""}`}>{v}</span>
    </div>
  );
}
